import { ParsedUrlQuery } from 'querystring'

import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import { usePageStateStore } from '@src/components/pl-base-widget/contexts/page-state-context'
import { createLayoutDataFetchingService } from '@src/components/pl-base-widget/utils/fetch-layout-data'
import { defaultPaginationCacheAdapter } from '@src/components/pl-base-widget/utils/pagination-cache-adapters'
import { usePLPageMeta } from '@src/components/pl-page-context'
import { useStatsigCollectionFiltersConfig } from '@src/experiments/statsig-integration/use-statsig-collection-filters-config.hook'
import { ALL_SUBCOLLECTION_FILTER_ID } from '@src/pages/[...slug]/components/collection/collection.constants'
import { SelectedFilter } from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import { getCollectionCacheKey } from '@src/pages/[...slug]/components/collection/utils'
import { SORT_BY } from '@src/quince-components/src/atoms/filterSection/constants/constants'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { useIsSvdEnabled } from '@src/quince-site-common/src/hooks/use-is-svd-enabled'
import { createTargetURL } from '@src/quince-site-common/src/hooks/useQueryState'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { serializeFilters } from '@src/utils/filter.utils'
import { pickBy } from '@src/utils/functions/utils'

import type { SeoData } from '@lastbrand/storefront-proto-registry/api/v1/seo'

const ALL_SUBCOLLECTION_IDS = [ALL_SUBCOLLECTION_FILTER_ID, 'All', 'all'] as const

// Checks if a value represents the "All" subcollection option
function isAllSubcollectionValue(value: string): boolean {
  return ALL_SUBCOLLECTION_IDS.some(val => val.toLowerCase() === value.toLowerCase())
}

function buildSearchParamsFromQuery(query: ParsedUrlQuery): string {
  const params = new URLSearchParams()
  const keys = Object.keys(query).sort()

  keys.forEach(key => {
    const value = query[key]
    if (typeof value === 'string') {
      params.set(key, value)
      return
    }
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (typeof item === 'string') {
          params.append(key, item)
        }
      })
    }
  })

  return params.toString()
}

interface FilterUpdate {
  filterId: string
  values: string[]
  mode: 'add' | 'remove' | 'toggle' | 'replace'
}

export type ClearAllFiltersOptions = Pick<UpdateURLOptions, 'preserveScrollPosition'> & {
  /** When false, clears URL filters without emitting filters_cleared (subcollection programmatic clears). Default true. */
  trackFiltersCleared?: boolean
}

interface UseCLPFiltersReturn {
  selectedFilters: Record<string, string[]>
  sortBy: string
  updateFilter: (update: FilterUpdate) => void
  updateSort: (sortValue: string) => void
  clearAllFilters: (options?: ClearAllFiltersOptions) => void
  setOnlyFilter: (
    filterId: string,
    values: string[],
    options?: Pick<UpdateURLOptions, 'preserveScrollPosition'>,
  ) => void
  setFilters: (newFilters: Record<string, string[]>) => void
}

interface RefreshOptions {
  forceNetwork?: boolean
}

interface UpdateURLOptions {
  forceNetworkRefresh?: boolean
  preserveScrollPosition?: boolean
}

export function useCLPFilters({
  pageMetaData,
  onSeoDataUpdate,
}: {
  pageMetaData: { title: string; slug: string }
  onSeoDataUpdate?: (seoData: SeoData | undefined) => void
}): UseCLPFiltersReturn {
  const router = useRouter()
  const pageMeta = usePLPageMeta()
  const pageStateStore = usePageStateStore()
  // TEMPORARY (STRFRNT-15810): ask PL for SVD-shaped cards on filter/sort refresh too — see
  // `SVD_ENRICH_VERSION_HEADER` doc.
  const svdEnrichHeaderEnabled = useIsSvdEnabled()
  // SSR stashes the Statsig FilterVersionConfig on pageMeta; the client hook is the
  // safety net for cases where SSR data was missing (non-PL → PL transitions) and
  // returns the local FILTER_VERSION_CONFIG default on its own.
  const statsigFilterVersionConfig = useStatsigCollectionFiltersConfig()
  const filterVersionConfig = pageMeta.filterVersionConfig ?? statsigFilterVersionConfig
  const lastFetchedSearchParamsRef = useRef<string | null>(null)
  const hasInitializedRef = useRef(false)
  /** When set, we just pushed in updateURL; effect syncs ref to this and skips refetch. Prevents intermittent race where effect runs with stale router.query and refetches with old params (any filter/sort change). */
  const pendingSearchParamsRef = useRef<string | null>(null)
  /** SSR-supplied pathname. When it changes, getServerSideProps re-ran (path-mode subcoll click, cross-collection nav) and useLayoutPagination already resyncs the store from initialWidgets — no CSR refetch needed. */
  const lastSeenSSRPathnameRef = useRef(pageMeta.pathname)

  // Single source of truth for both the parsed filters AND the next-write version.
  // Deriving both from one reader call ensures we never disagree with ourselves about
  // which key won (matters when both `filter` and `filters` are transiently in the
  // URL, or when neither is present and the reader falls back to queryUpdateVersion.default).
  const readerResult = useMemo(() => {
    return SelectedFilter.getSelectedFilterFromQueryParamsWithV2Support(
      (router.query.filter as string | undefined) ?? null,
      (router.query.filters as string | undefined) ?? null,
      filterVersionConfig,
    )
  }, [router.query.filter, router.query.filters, filterVersionConfig])

  const selectedFilters = useMemo(() => {
    const out: Record<string, string[]> = {}
    for (const [key, value] of Object.entries(readerResult.selectedFilter)) {
      if (Array.isArray(value) && value.length > 0) out[key] = value
    }
    return out
  }, [readerResult])

  // isV2Format is the reader's contract for "the next write should be v2". It already
  // bakes in versionPriority (both-keys case) and queryUpdateVersion.default (no-keys case).
  const isV2WriteFormat = readerResult.isV2Format

  const sortBy = useMemo(() => {
    return (
      (router.query.sortBy as string | undefined) ?? (router.query.sort as string | undefined) ?? SORT_BY.FEATURED.value
    )
  }, [router.query.sort, router.query.sortBy])

  const applyLayoutState = useCallback(
    (layoutState: Parameters<typeof defaultPaginationCacheAdapter.setLayoutPaginationState>[1]) => {
      const { layoutPaginationActions, widgetStateActions } = pageStateStore.getState()
      layoutPaginationActions.resetLayoutPagination()
      if (layoutState.accumulatedWidgets.length > 0) {
        layoutPaginationActions.appendWidgets(layoutState.accumulatedWidgets)
      }
      layoutPaginationActions.setLayoutPaginationInfo(layoutState.paginationInfo)
      widgetStateActions.clearWidgetStates()
    },
    [pageStateStore],
  )

  const refreshLayoutData = useCallback(
    async (searchParams: string, options: RefreshOptions = {}) => {
      const cacheKey = getCollectionCacheKey(pageMeta.pathname, searchParams)
      if (!options.forceNetwork) {
        const cachedLayoutState = defaultPaginationCacheAdapter.getLayoutPaginationState(cacheKey)
        if (cachedLayoutState) {
          applyLayoutState(cachedLayoutState)
          return
        }
      }

      try {
        const fetcher = createLayoutDataFetchingService({ ...pageMeta, searchParams }, svdEnrichHeaderEnabled)
        const response = await fetcher.fetchMoreWidgets({})
        const layoutState = {
          accumulatedWidgets: response.widgets,
          paginationInfo: response.pagination,
        }

        applyLayoutState(layoutState)
        defaultPaginationCacheAdapter.publishCacheWithStates(
          cacheKey,
          typeof window !== 'undefined' ? window.scrollY : 0,
          undefined,
          layoutState,
        )

        if (onSeoDataUpdate) {
          const params = new URLSearchParams(searchParams)
          // v2-aware so the SEO refresh keys off the same subcollections value the rest
          // of the request used, regardless of whether the URL is in v1 or v2 form.
          const { selectedFilter } = SelectedFilter.getSelectedFilterFromQueryParamsWithV2Support(
            params.get('filter'),
            params.get('filters'),
            filterVersionConfig,
          )
          const subcollections = selectedFilter.subcollections
          const hasSubcollectionFilter = Array.isArray(subcollections) && subcollections.length === 1
          onSeoDataUpdate(hasSubcollectionFilter ? response.meta?.seoData : undefined)
        }
      } catch (error) {
        console.error('Layout Data Refresh Failed', error)
        captureException(error instanceof Error ? error : new Error(String(error)), {
          module: MODULES.PL,
          feature: FEATURES.LAYOUT_PAGINATION,
          additionalTags: {
            trigger: 'filter_sort_refresh',
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            pageType: pageMeta.pageType ?? 'unknown',
            pathname: pageMeta.pathname,
          },
          extraInfo: {
            source: 'useCLPFilters.refreshLayoutData',
            slug: pageMeta.seoData?.slug ?? pageMeta.pathname,
            searchParams,
            forceNetwork: options.forceNetwork ?? false,
          },
        })
      }
    },
    [applyLayoutState, filterVersionConfig, onSeoDataUpdate, pageMeta, svdEnrichHeaderEnabled],
  )

  // Updates router query params with new filters/sort without triggering SSR, then refreshes layout
  const updateURL = useCallback(
    (newFilters: Record<string, string[]>, newSort: string, options: UpdateURLOptions = {}) => {
      const pathname = router.pathname
      const query = { ...router.query }

      // The reader is the single source of truth for "next write version" — it bakes in
      // versionPriority (both-keys case) and queryUpdateVersion.default (no-keys case).
      // ALWAYS strip the inactive key so the URL never carries both `filter` and
      // `filters` (PL would see a split body too). String literals are URL keys,
      // not user-facing copy.
      // eslint-disable-next-line i18next/no-literal-string
      const writeKey: 'filter' | 'filters' = isV2WriteFormat ? 'filters' : 'filter'
      // eslint-disable-next-line i18next/no-literal-string
      const inactiveKey: 'filter' | 'filters' = isV2WriteFormat ? 'filter' : 'filters'

      const filterStr = isV2WriteFormat
        ? SelectedFilter.getUpdatedSelectedFilterStringForWindowURLV2(new SelectedFilter(newFilters))
        : serializeFilters(newFilters)

      if (filterStr) {
        query[writeKey] = filterStr
      } else {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete query[writeKey]
      }
      // Strip the inactive key on every update so a stale URL parameter can't bleed
      // into the next fetch (which would force a double-read on the SSR / fetcher side).
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete query[inactiveKey]

      if (newSort && newSort !== SORT_BY.FEATURED.value) {
        query.sortBy = newSort
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete query.sort
      } else {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete query.sort
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete query.sortBy
      }

      const nextSearchParams = buildSearchParamsFromQuery(query)
      pendingSearchParamsRef.current = nextSearchParams

      const pushOptions = options.preserveScrollPosition ? { shallow: true, scroll: false } : { shallow: true }

      // Build the address-bar URL via `createTargetURL` (same helper the non-PL slug route
      // uses) so the v2 value's `,` delimiter stays raw — Next.js's default object→query
      // serializer would percent-encode it to `%2C`. We still pass the object form as the
      // first arg so the Next.js route resolution (and shallow-routing path) is unchanged;
      // only the visible URL is overridden.
      //
      // IMPORTANT: read the unwatched params from `router.asPath` (the visible URL's query
      // string), NOT `router.query`. On dynamic routes like `/[...slug]`, Pages Router
      // merges path params (e.g. `slug: ['men', 'sweaters']`) into `router.query`, and
      // iterating it would leak `slug=men&slug=sweaters` into the address bar.
      const queryIdx = router.asPath.indexOf('?')
      const pathSlice = queryIdx >= 0 ? router.asPath.slice(0, queryIdx) : router.asPath
      const visiblePath = pathSlice
      const rawQueryString = queryIdx >= 0 ? (router.asPath.slice(queryIdx + 1).split('#')[0] ?? '') : ''
      const currentURLParams = new URLSearchParams(rawQueryString)
      const unwatchedParams = new URLSearchParams()
      currentURLParams.forEach((value, key) => {
        if (key === 'filter' || key === 'filters' || key === 'sort' || key === 'sortBy') return
        unwatchedParams.append(key, value)
      })
      const asUrl =
        visiblePath +
        createTargetURL({
          updatedWatchedQueryState: {
            filter: typeof query.filter === 'string' ? query.filter : undefined,
            filters: typeof query.filters === 'string' ? query.filters : undefined,
            sortBy: typeof query.sortBy === 'string' ? query.sortBy : undefined,
            sort: undefined,
          },
          searchParams: unwatchedParams,
          // URL keys, not user-facing copy.
          // eslint-disable-next-line i18next/no-literal-string
          queryKeys: ['filter', 'filters', 'sort', 'sortBy'],
        })

      void router.push(
        {
          pathname,
          query,
        },
        asUrl,
        pushOptions,
      )

      void refreshLayoutData(nextSearchParams, { forceNetwork: options.forceNetworkRefresh })
    },
    [isV2WriteFormat, refreshLayoutData, router],
  )

  // Updates a single filter with add/remove/toggle/replace modes and syncs to URL
  const updateFilter = useCallback(
    (update: FilterUpdate) => {
      const newFilters = { ...selectedFilters }
      const currentValues = newFilters[update.filterId] ?? []

      switch (update.mode) {
        case 'add':
          newFilters[update.filterId] = [...new Set([...currentValues, ...update.values])]
          break
        case 'remove': {
          const findRemovedFilter = currentValues.find(v => update.values.includes(v))
          trackEvent(TELEMETRY_EVENT_NAMES.INDIVIDUAL_FILTERS_CLEARED, {
            filter_key: update.filterId,
            filter_value: findRemovedFilter,
          })

          const filteredValues = currentValues.filter(v => !update.values.includes(v))
          if (filteredValues.length === 0) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete newFilters[update.filterId]
          } else {
            newFilters[update.filterId] = filteredValues
          }
          break
        }
        case 'toggle': {
          const toggledValues = [...currentValues]
          update.values.forEach(value => {
            const index = toggledValues.indexOf(value)
            if (index >= 0) {
              toggledValues.splice(index, 1)
            } else {
              toggledValues.push(value)
            }
          })
          if (toggledValues.length > 0) {
            newFilters[update.filterId] = toggledValues

            trackEvent(TELEMETRY_EVENT_NAMES.FILTER_CLICK, {
              label: update.filterId,
              value: update.values[0],
              context: {
                collectionSlug: pageMetaData.slug,
                collectionTitle: pageMetaData.title,
                isSelected: true,
                selectedFilters: pickBy(newFilters as Record<string, unknown>, v => v !== undefined),
                filter_key: update.filterId,
                filter_value: update.values[0],
              },
            })
          } else {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete newFilters[update.filterId]
          }
          break
        }
        case 'replace':
          if (update.values.length > 0) {
            newFilters[update.filterId] = update.values
          } else {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete newFilters[update.filterId]
          }
          break
      }

      // Ensure values like "all" are never added to the URL
      Object.keys(newFilters).forEach(key => {
        const values = newFilters[key]
        if (values) {
          const filteredValues = values.filter(v => !isAllSubcollectionValue(v))
          if (filteredValues.length === 0) {
            // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
            delete newFilters[key]
          } else {
            newFilters[key] = filteredValues
          }
        }
      })

      updateURL(newFilters, sortBy)
    },
    [pageMetaData.slug, pageMetaData.title, selectedFilters, sortBy, updateURL],
  )

  // Updates sort parameter in URL while preserving current filters
  const updateSort = useCallback(
    (sortValue: string) => {
      // No-op when user re-selects currently active sort option.
      if (sortValue === sortBy) {
        return
      }

      const isDefaultOption = sortValue === SORT_BY.FEATURED.value
      if (!isDefaultOption) {
        trackEvent(TELEMETRY_EVENT_NAMES.SORT_USED, {
          sort_used: sortValue,
        })
      }
      // Always hit API on sort changes to avoid stale ordering from cache.
      updateURL(selectedFilters, sortValue, { forceNetworkRefresh: true })
    },
    [selectedFilters, sortBy, updateURL],
  )

  // Clears all filters from URL while preserving current sort
  const clearAllFilters = useCallback(
    (options?: ClearAllFiltersOptions) => {
      if (options?.trackFiltersCleared !== false) {
        trackEvent(TELEMETRY_EVENT_NAMES.FILTERS_CLEARED, {})
      }
      updateURL({}, sortBy, { preserveScrollPosition: options?.preserveScrollPosition })
    },
    [sortBy, updateURL],
  )

  // Sets only one filter (clearing all others) and filters out "All" values
  const setOnlyFilter = useCallback(
    (filterId: string, values: string[], options?: Pick<UpdateURLOptions, 'preserveScrollPosition'>) => {
      const newFilters: Record<string, string[]> = {}
      const filteredValues = values.filter(v => !isAllSubcollectionValue(v))
      if (filteredValues.length > 0) {
        newFilters[filterId] = filteredValues
      }
      updateURL(newFilters, sortBy, { preserveScrollPosition: options?.preserveScrollPosition })
    },
    [sortBy, updateURL],
  )

  // Replaces all current filters with a new set of filters
  const setFilters = useCallback(
    (newFilters: Record<string, string[]>) => {
      // Filter out "all" values from all filter groups
      const sanitizedFilters: Record<string, string[]> = {}
      Object.entries(newFilters).forEach(([key, values]) => {
        const filteredValues = values.filter(v => !isAllSubcollectionValue(v))
        if (filteredValues.length > 0) {
          sanitizedFilters[key] = filteredValues
        }
      })
      updateURL(sanitizedFilters, sortBy)
    },
    [sortBy, updateURL],
  )

  // Handle back/forward navigation by re-fetching layout data for the URL.
  // When we pushed in updateURL, pending is set: sync ref to intended params and skip refetch
  // (refresh already ran in updateURL; avoids refetching with stale router.query).
  useEffect(() => {
    if (!router.isReady) return

    const currentSearchParams = buildSearchParamsFromQuery(router.query)

    if (pendingSearchParamsRef.current !== null) {
      lastFetchedSearchParamsRef.current = pendingSearchParamsRef.current
      pendingSearchParamsRef.current = null
      lastSeenSSRPathnameRef.current = pageMeta.pathname
      return
    }

    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true
      lastFetchedSearchParamsRef.current = currentSearchParams
      lastSeenSSRPathnameRef.current = pageMeta.pathname
      return
    }

    // SSR-driven nav (path-mode subcoll, cross-collection, full back/forward) already populated
    // fresh widgets, and useLayoutPagination resyncs the store from initialWidgets — skip CSR refetch.
    if (lastSeenSSRPathnameRef.current !== pageMeta.pathname) {
      lastFetchedSearchParamsRef.current = currentSearchParams
      lastSeenSSRPathnameRef.current = pageMeta.pathname
      return
    }

    if (lastFetchedSearchParamsRef.current === currentSearchParams) {
      return
    }

    lastFetchedSearchParamsRef.current = currentSearchParams
    void refreshLayoutData(currentSearchParams)
  }, [refreshLayoutData, router.isReady, router.query, pageMeta.pathname])

  return {
    selectedFilters,
    sortBy,
    updateFilter,
    updateSort,
    clearAllFilters,
    setOnlyFilter,
    setFilters,
  }
}
